import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useNavigate } from "react-router-dom";
import {
  XMarkIcon,
  CalendarDaysIcon,
  ChatBubbleLeftEllipsisIcon,
  PhoneIcon,
  VideoCameraIcon,
  EnvelopeIcon,
  CheckIcon,
  InformationCircleIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button, Skeleton } from "components/ui";
import { Switch } from "components/ui/Form";
import { getUserIdFromToken, getDecodedToken } from "utils/tokenUtils";
import { useActivityTypes, useFollowUpActions, useActivityById, useFollowUpBulkActions } from "hooks/useFollowUp";
import { getAllLeads } from "services/queryService";
import { updateLeadOwner } from "services/leadsService";
import { getInternalUsersForAssigningLeads } from "services/userService";
import { upsertCustomActivity } from "services/followUpService";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { toast } from "sonner";
import CalendarSection from "./CalendarSection";
import DetailsSection from "./DetailsSection";
import { ActivityDetailSkeleton } from "custom-components/shared/Skeletons/activityDetailSkeleton";
import clsx from "clsx";
import { timeSlots } from "constants/timeSlots.constant";
import { getTemplates } from "services/templateService";
import { canEditFollowUp, canDeleteFollowUp } from "permissions/permissionHelpers";
import { useAuthContext } from "app/contexts/auth/context";
import { getNormalizedActivityStatus } from "constants/activityStatus.constant";
import { formatNumber } from "utils/formatNumber";



const stripHtml = (htmlString) => {
  if (!htmlString) return "";
  return htmlString
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .trim();
};

const extractOwnerId = (leadData) => {
  if (!leadData) return null;

  if (Array.isArray(leadData.Owners) && leadData.Owners.length > 0) {
    const primaryOwner = leadData.Owners.find(o => o.isPrimary);
    if (primaryOwner) {
      return String(primaryOwner.OwnerId || "");
    }
    const firstOwner = leadData.Owners[0];
    return String(firstOwner?.OwnerId || "");
  }

  if (leadData.SourceDetails?.LeadUserId) return String(leadData.SourceDetails.LeadUserId);

  return null;
};



const FollowUpScheduleModal = ({
  isOpen,
  onClose,
  leadData,
  onSuccess,
  enableLeadSelection = false,
  selectedDate: preSelectedDate = null,
  editingFollowUp = null,
  preSelectedActivityType = null,  // { channelId, label } — skip type-selection step
  restrictedEditMode = false,       // When true: only Activity Name, Status, Remarks are editable
  emailTemplates,
  smsTemplates,
  isJobActivity = false,
}) => {
  const { user: authUser } = useAuthContext();
  const currentLoggedInUserId = useMemo(() => {
    return authUser?.UserId || authUser?.userId || authUser?.id || getUserIdFromToken() || "";
  }, [authUser]);
  const isAdminOrOwner = useMemo(() => {
    const userRole = (authUser?.UserRole || "").toString().trim().toLowerCase();
    return (
      userRole.includes("admin") ||
      userRole.includes("owner")
    );
  }, [authUser]);
  const followUpActions = useFollowUpActions({ leadId: leadData?.id || editingFollowUp?.leadId });
  const isEditMode = (editingFollowUp && !editingFollowUp.isReschedule) || restrictedEditMode;
  const followUpIdToFetch = editingFollowUp?.id || editingFollowUp?.Id || editingFollowUp?.FollowUpId || editingFollowUp?.followUpId;
  const { activity: fetchedActivity, isLoading: loadingActivity, error: activityError } = useActivityById(isOpen && followUpIdToFetch ? followUpIdToFetch : null);

  const isActivityLoading = useMemo(() => {
    if (!isOpen || !editingFollowUp) return false;
    if (activityError) return false;
    const targetId = editingFollowUp.id || editingFollowUp.Id || editingFollowUp.FollowUpId || editingFollowUp.followUpId;
    if (loadingActivity) return true;
    if (!fetchedActivity) return true;
    const currentId = fetchedActivity.id || fetchedActivity.Id || fetchedActivity.FollowUpId || fetchedActivity.followUpId;
    return String(currentId).toLowerCase() !== String(targetId).toLowerCase();
  }, [isOpen, editingFollowUp, loadingActivity, fetchedActivity, activityError]);

  const [localEmailTemplates, setLocalEmailTemplates] = useState([]);
  const [localSmsTemplates, setLocalSmsTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Fetch templates if not provided as props
  useEffect(() => {
    if (isOpen && (!emailTemplates || emailTemplates.length === 0 || !smsTemplates || smsTemplates.length === 0)) {
      const fetchTemplates = async () => {
        setTemplatesLoading(true);
        try {
          const [emailRes, smsRes] = await Promise.all([
            getTemplates({ pageNumber: 1, pageSize: 50, channelId: 1, search: "" }),
            getTemplates({ pageNumber: 1, pageSize: 50, channelId: 2, search: "" }),
          ]);
          if (emailRes.success) {
            setLocalEmailTemplates(emailRes.data);
          }
          if (smsRes.success) {
            setLocalSmsTemplates(smsRes.data);
          }
        } catch (err) {
          console.error("Failed to fetch templates in FollowUpScheduleModal:", err);
        } finally {
          setTemplatesLoading(false);
        }
      };
      fetchTemplates();
    }
  }, [isOpen, emailTemplates, smsTemplates]);

  const resolvedEmailTemplates = emailTemplates && emailTemplates.length > 0 ? emailTemplates : localEmailTemplates;
  const resolvedSmsTemplates = smsTemplates && smsTemplates.length > 0 ? smsTemplates : localSmsTemplates;

  const mappedEmailTemplates = useMemo(() => {
    return (resolvedEmailTemplates || []).map(t => ({
      ...t,
      id: t.TemplateId || t.id,
      label: t.name || t.Name,
      body: t.body || t.Body,
      subject: t.subject || t.Subject
    }));
  }, [resolvedEmailTemplates]);

  const mappedSmsTemplates = useMemo(() => {
    return (resolvedSmsTemplates || []).map(t => ({
      ...t,
      id: t.TemplateId || t.id,
      label: t.name || t.Name,
      body: t.body || t.Body,
      subject: t.subject || t.Subject
    }));
  }, [resolvedSmsTemplates]);

  const [step, setStep] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [selectedTime, setSelectedTime] = useState("09:00");
  const [followUpType, setFollowUpType] = useState(99);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const isCustomActivity = useMemo(() => {
    const rawChannelId = editingFollowUp?.channelId ?? editingFollowUp?.ChannelId ?? fetchedActivity?.ChannelId ?? selectedChannelId;
    const rawLeadId = editingFollowUp?.leadId ?? editingFollowUp?.LeadId ?? fetchedActivity?.LeadId;
    if (followUpType === 99 || selectedChannelId === 99 || rawChannelId === 99) return true;
    if ((rawChannelId === null || rawChannelId === undefined) && (rawLeadId === null || rawLeadId === undefined) && (editingFollowUp || fetchedActivity)) return true;
    return false;
  }, [followUpType, selectedChannelId, editingFollowUp, fetchedActivity]);
  const [notes, setNotes] = useState("");
  const [assignedTo, setAssignedTo] = useState(String(currentLoggedInUserId) || "");
  const [statusId, setStatusId] = useState(1);

  const isAlreadyCompleted = useMemo(() => {
    if (isActivityLoading) return false;
    if (!editingFollowUp && !fetchedActivity) return false;
    const sId = editingFollowUp?.StatusId ?? editingFollowUp?.statusId ?? editingFollowUp?._raw?.StatusId ?? fetchedActivity?.StatusId ?? fetchedActivity?.statusId;
    return sId === 2;
  }, [editingFollowUp, fetchedActivity, isActivityLoading]);


  const [attendees, setAttendees] = useState([]);

  const isPermissionReadOnly = useMemo(() => {
    // 1. Owner / Admin / IsAdmin has full access (always editable)
    if (isAdminOrOwner) {
      return false;
    }

    // 2. When creating a new activity (not editing an existing one), read-only mode does not apply
    if (!editingFollowUp && !fetchedActivity) {
      return false;
    }

    const currentUserId = authUser?.UserId || authUser?.userId || authUser?.id;

    if (currentUserId) {
      const assignedToId =
        assignedTo ||
        fetchedActivity?.AssignedToId ||
        fetchedActivity?.assignedToId ||
        fetchedActivity?.assignedTo ||
        fetchedActivity?.AssignedTo ||
        editingFollowUp?.AssignedToId ||
        editingFollowUp?.assignedToId ||
        editingFollowUp?.assignedTo ||
        editingFollowUp?.AssignedTo;

      const isAssignee = assignedToId && String(currentUserId).toLowerCase() === String(assignedToId).toLowerCase();

      // 3. Assigned User (Non-Admin): standard permission applies
      if (isAssignee) {
        return !canEditFollowUp();
      }

      // 4. Attendee Only (Non-Admin): MUST be view-only
      const isAttendee = attendees && attendees.some(
        (id) => String(id).toLowerCase() === String(currentUserId).toLowerCase()
      );

      if (isAttendee) {
        return true;
      }
    }

    // 5. Other Users: standard permission applies
    return !canEditFollowUp();
  }, [editingFollowUp, fetchedActivity, authUser, attendees, assignedTo, isAdminOrOwner]);

  // Attendee-only view: user is an attendee but not admin/owner/assignee
  const isAttendeeViewOnly = useMemo(() => {
    if (!editingFollowUp && !fetchedActivity) {
      return false;
    }

    if (isAdminOrOwner) return false;

    const currentUserId = authUser?.UserId || authUser?.userId || authUser?.id;

    const assignedToId =
      assignedTo ||
      fetchedActivity?.AssignedToId ||
      fetchedActivity?.assignedToId ||
      fetchedActivity?.assignedTo ||
      fetchedActivity?.AssignedTo ||
      editingFollowUp?.AssignedToId ||
      editingFollowUp?.assignedToId ||
      editingFollowUp?.assignedTo ||
      editingFollowUp?.AssignedTo;

    const isAssignee = assignedToId && currentUserId && String(currentUserId).toLowerCase() === String(assignedToId).toLowerCase();
    if (isAssignee) return false;

    if (!currentUserId || !attendees || attendees.length === 0) return false;
    return attendees.some(
      (id) => String(id).toLowerCase() === String(currentUserId).toLowerCase()
    );
  }, [authUser, attendees, fetchedActivity, editingFollowUp, assignedTo, isAdminOrOwner]);

  const isReadOnlyMode = isAlreadyCompleted || isPermissionReadOnly;

  // Programmatic DOM disablement for read-only mode to prevent keyboard focus/typing/submission
  useEffect(() => {
    if (isReadOnlyMode && isOpen) {
      const handleDisable = () => {
        const modalElement = document.querySelector('[role="dialog"]');
        if (modalElement) {
          // 1. Disable all inputs, selects, textareas to block keyboards/focus
          const inputs = modalElement.querySelectorAll('input, select, textarea');
          inputs.forEach(el => {
            el.setAttribute('disabled', 'true');
            el.setAttribute('readonly', 'true');
            el.style.pointerEvents = 'none';
            if (document.activeElement === el) {
              el.blur(); // Dismiss active focus/cursor
            }
          });

          // 2. Disable contenteditable areas (rich text editors)
          const editables = modalElement.querySelectorAll('[contenteditable="true"]');
          editables.forEach(el => {
            el.setAttribute('contenteditable', 'false');
            el.style.pointerEvents = 'none';
            if (document.activeElement === el) {
              el.blur();
            }
          });

          // 3. Hide or disable Save/Update/Reschedule/Delete action buttons
          const buttons = modalElement.querySelectorAll('button');
          buttons.forEach(btn => {
            const text = (btn.textContent || "").toLowerCase().trim();
            const isSaveAction =
              text.includes('update') ||
              text.includes('schedule') ||
              text.includes('reschedule') ||
              text.includes('save') ||
              text.includes('delete') ||
              text === 'ok' ||
              text === 'confirm';

            if (isSaveAction) {
              btn.setAttribute('disabled', 'true');
              btn.style.display = 'none';
            }
          });
        }
      };

      handleDisable();
      const interval = setInterval(handleDisable, 200);
      return () => clearInterval(interval);
    }
  }, [isReadOnlyMode, isOpen, isActivityLoading, attendees]);

  const [reminderType, setReminderType] = useState("none");
  const [isAutomated, setIsAutomated] = useState(false);
  const [includeLead, setIncludeLead] = useState(true);
  const [currentViewDate, setCurrentViewDate] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadActivityTypeId, setLeadActivityTypeId] = useState(null);
  const [selectedLead, setSelectedLead] = useState([]);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [errors, setErrors] = useState({});
  // ── Direct API call for internal users for assigning leads ──
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await getInternalUsersForAssigningLeads();
        if (isMounted) {
          if (response && response.success) {
            setAvailableUsers(response.data || []);
          } else {
            setAvailableUsers([]);
          }
        }
      } catch (error) {
        console.error("Error fetching internal users for assignment:", error);
        if (isMounted) {
          setAvailableUsers([]);
        }
      } finally {
        if (isMounted) {
          setLoadingUsers(false);
        }
      }
    };

    if (isOpen) {
      fetchUsers();
    }

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  const { types: apiActivityTypes, isLoading: loadingTypes } = useActivityTypes();
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignState, setReassignState] = useState("pending");
  const [pendingAssignedTo, setPendingAssignedTo] = useState("");
  const [reassignLoading, setReassignLoading] = useState(false);
  const [followUpName, setFollowUpName] = useState("");
  const [title, setTitle] = useState("");
  // Granular States for Activity Composer
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [smsBody, setSmsBody] = useState("");
  const [ccList, setCcList] = useState([]);
  const [bccList, setBccList] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [emailTemplate, setEmailTemplate] = useState({ id: 1, label: "-- No Template --" });
  // Granular States for Activity Details
  const [phoneNumber, setPhoneNumber] = useState("");
  const [meetingUrl, setMeetingUrl] = useState("");
  const [address, setAddress] = useState("");
  const [formattedLeadActivityNotes, setFormattedLeadActivityNotes] = useState("");

  // Granular States for Preferred Schedule Delivery Date & Time
  const [preferredDeliveryDate, setPreferredDeliveryDate] = useState(null);
  const [preferredDeliveryTime, setPreferredDeliveryTime] = useState("");
  const [isScheduleDeliveryEnabled, setIsScheduleDeliveryEnabled] = useState(false);

  // Lead data for variable replacement in composer
  const selectedLeadObj = useMemo(() => {
    if (Array.isArray(selectedLead) && selectedLead.length > 0) {
      // selectedLead is [leadId, ...contactIds] — find the lead whose ID is first/included
      const found = availableLeads.find(l => selectedLead.includes(l.value));
      if (found) {
        let phoneVal = found.phone;
        let emailVal = found.email;
        let nameVal = found.name;

        if (found.contacts && found.contacts.length > 0) {
          const selectedContactKey = selectedLead.find(key => typeof key === "string" && key.startsWith(`${found.value}::`));
          if (selectedContactKey) {
            const contactId = selectedContactKey.split("::")[1];
            const matchedContact = found.contacts.find(c => String(c.ContactId || c.contactId || c.id || c.value) === String(contactId));
            if (matchedContact) {
              phoneVal = matchedContact.PhoneNumber;
              emailVal = matchedContact.Email;
              nameVal = matchedContact.FullName;
            }
          } else {
            const primary = found.contacts[0];
            phoneVal = primary.PhoneNumber;
            emailVal = primary.Email;
            nameVal = primary.FullName;
          }
        }

        return {
          id: found.value,
          name: nameVal,
          email: emailVal,
          phone: phoneVal || ""
        };
      }
    }

    // Check if we are editing and have lead details in fetchedActivity or editingFollowUp
    const currentActivity = isActivityLoading ? null : fetchedActivity;
    if (currentActivity && (currentActivity.LeadName)) {
      return {
        id: currentActivity.LeadId,
        name: currentActivity.LeadName,
        email: currentActivity.LeadEmail,
        phone: currentActivity.CallPhoneNumber || currentActivity.LeadPhone || currentActivity.phoneNumber || ""
      };
    }

    if (leadData) {
      const primaryContact = leadData.Contacts?.[0] || leadData.contacts?.[0];
      const phoneVal = leadData.PhoneNumber || "";
      const emailVal = leadData.Email || "";
      const nameVal = primaryContact?.FullName || "";

      return {
        id: leadData.id || leadData.leadId || leadData.Id,
        name: nameVal,
        email: emailVal,
        phone: phoneVal
      };
    }

    return null;
  }, [enableLeadSelection, selectedLead, availableLeads, leadData, fetchedActivity, editingFollowUp, isActivityLoading]);

  const navigate = useNavigate();
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canUserDelete = useMemo(() => {
    return isAdminOrOwner || canDeleteFollowUp();
  }, [isAdminOrOwner]);

  const targetLeadId = useMemo(() => {
    if (leadData) {
      const lId = leadData.id || leadData.leadId || leadData.Id;
      if (Array.isArray(lId) && lId.length > 0) return lId[0];
      if (lId && typeof lId !== 'object') return lId;
    }
    if (editingFollowUp) {
      const lId = editingFollowUp.leadId || editingFollowUp.LeadId;
      if (lId) return lId;
    }
    if (fetchedActivity) {
      const lId = fetchedActivity.leadId || fetchedActivity.LeadId;
      if (lId) return lId;
    }
    if (selectedLeadObj?.id) {
      return selectedLeadObj.id;
    }
    if (Array.isArray(selectedLead) && selectedLead.length > 0) {
      const firstVal = String(selectedLead[0]);
      const leadIdPart = firstVal.includes("::") ? firstVal.split("::")[0] : firstVal;
      if (leadIdPart && leadIdPart !== 'undefined' && leadIdPart !== 'null') return leadIdPart;
    }
    return null;
  }, [leadData, editingFollowUp, fetchedActivity, selectedLeadObj, selectedLead]);

  const hasMultipleLeads = useMemo(() => {
    if (leadData) {
      const lId = leadData.id || leadData.leadId || leadData.Id;
      if (Array.isArray(lId) && lId.length > 1) return true;
    }

    if (Array.isArray(selectedLead) && selectedLead.length > 0) {
      const uniqueLeadIds = new Set();
      selectedLead.forEach((item) => {
        const itemStr = String(item);
        const leadIdPart = itemStr.includes("::") ? itemStr.split("::")[0] : itemStr;
        if (leadIdPart && leadIdPart !== "undefined" && leadIdPart !== "null") {
          uniqueLeadIds.add(leadIdPart);
        }
      });
      if (uniqueLeadIds.size > 1) return true;
    }

    return false;
  }, [leadData, selectedLead]);

  const handleNavigateToLead = () => {
    if (!targetLeadId) return;
    window.open(`/leads/opportunities/${targetLeadId}`, "_blank", "noopener,noreferrer");
  };

  const { handleBulkAction } = useFollowUpBulkActions();

  const handleDeleteActivity = async () => {
    const targetId =
      editingFollowUp?.id ||
      editingFollowUp?.Id ||
      editingFollowUp?.FollowUpId ||
      editingFollowUp?.followUpId ||
      fetchedActivity?.id ||
      fetchedActivity?.Id ||
      fetchedActivity?.FollowUpId ||
      fetchedActivity?.followUpId ||
      followUpIdToFetch;

    if (!targetId) return;

    setIsDeleting(true);
    try {
      const result = await handleBulkAction({
        actionType: 2,
        followUpIds: [targetId],
      });
      if (result?.success) {
        toast.success(result?.message || "Activity deleted successfully");
        setShowDeleteConfirmModal(false);
        if (onSuccess) {
          onSuccess({ type: "delete", id: targetId });
        }
        onClose();
      } else {
        toast.error(result?.error || result?.message || "Failed to delete activity");
      }
    } catch (error) {
      console.error("Error deleting activity:", error);
      toast.error(error.message || "Failed to delete activity");
    } finally {
      setIsDeleting(false);
    }
  };

  // Auto-populate phone number when Phone activity (type 3) is selected or selected lead/contact changes
  useEffect(() => {
    if (!isEditMode && (followUpType === 3 || selectedChannelId === 3) && selectedLeadObj?.phone) {
      setPhoneNumber(formatNumber(selectedLeadObj.phone));
    }
  }, [followUpType, selectedChannelId, selectedLeadObj, isEditMode]);

  const displayLeadName = useMemo(() => {
    if (editingFollowUp) {
      return editingFollowUp.userName || editingFollowUp.LeadName || "Lead";
    }
    if (leadData) {
      const lId = leadData.id || leadData.leadId;
      if (lId) {
        if (Array.isArray(lId)) {
          if (lId.length === 1) {
            const found = availableLeads.find(l => String(l.value) === String(lId[0]));
            if (found) return found.name || found.label;
          }
          return `${lId.length} selected leads`;
        } else {
          const found = availableLeads.find(l => String(l.value) === String(lId));
          if (found) return found.name || found.label;
          return leadData.name || "Lead";
        }
      }
      return leadData.name || "Lead";
    }
    return null;
  }, [editingFollowUp, leadData, availableLeads]);


  // Load leads when modal opens (users now loaded via useAssignmentUsers hook)
  useEffect(() => {
    if (isOpen) {
      loadLeads();
    }
  }, [isOpen, enableLeadSelection]);

  // Sync emailTemplate with loaded templates to get full template details (body, subject, etc.)
  useEffect(() => {
    if (emailTemplate && emailTemplate.id && emailTemplate.id !== 1) {
      const templatesToSearch = selectedChannelId === 1 ? mappedEmailTemplates : (selectedChannelId === 2 ? mappedSmsTemplates : []);
      if (templatesToSearch.length > 0) {
        const found = templatesToSearch.find(t => String(t.id) === String(emailTemplate.id));
        if (found && (found.label !== emailTemplate.label || found.body !== emailTemplate.body || found.subject !== emailTemplate.subject)) {
          setEmailTemplate(found);
        }
      }
    }
  }, [mappedEmailTemplates, mappedSmsTemplates, selectedChannelId, emailTemplate]);


  // Reset form when modal opens or fill with editing data
  useEffect(() => {
    if (isOpen) {
      setErrors({});

      const currentData = fetchedActivity ? { ...editingFollowUp, ...fetchedActivity } : editingFollowUp;
      if (currentData) {
        setStep("schedule");
        // Pre-fill form with editing data
        // Handle Date - check all possible variations
        const editDateRaw =
          currentData.ScheduledDate;

        let editDate = new Date();
        if (editDateRaw) {
          // Extract just the date part (YYYY-MM-DD) to prevent timezone shifts
          const dateStr = typeof editDateRaw === 'string' ? editDateRaw.split('T')[0] : editDateRaw;
          // By replacing hyphens with slashes or appending T00:00:00, we can ensure it parses locally
          editDate = new Date(dateStr + 'T00:00:00');
        }
        setSelectedDate(editDate);

        // Handle Time - check all possible variations
        let timeValue =
          currentData.ScheduledTime;

        // Helper: convert "3:30 PM" / "03:30 PM" 12h format → "15:30" 24h HH:mm
        const convertTo24h = (t) => {
          if (!t || typeof t !== 'string') return t;
          const match = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
          if (match) {
            let h = parseInt(match[1], 10);
            const m = match[2];
            const period = match[3].toUpperCase();
            if (period === 'PM' && h !== 12) h += 12;
            if (period === 'AM' && h === 12) h = 0;
            return `${String(h).padStart(2, '0')}:${m}`;
          }
          // Already HH:mm or HH:mm:ss — strip seconds and normalize
          if (t.includes(':')) {
            const parts = t.split(':');
            return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
          }
          return t;
        };

        timeValue = convertTo24h(timeValue);
        setSelectedTime(timeValue);

        // Handle Follow-up Type / Channel
        let channelId = currentData.ChannelId ?? currentData.channelId;
        const currentLeadId = currentData.LeadId ?? currentData.leadId;
        if (channelId === null || channelId === undefined) {
          if (currentLeadId === null || currentLeadId === undefined || editingFollowUp?.channelId === 99 || editingFollowUp?.ChannelId === 99 || currentData.IsJobActivity) {
            channelId = 99;
          }
        }
        setFollowUpType(channelId);
        setSelectedChannelId(channelId);

        // Handle Status: Prefer outer item status over synthetic Overdue override from backend
        const getStatusFromObj = (obj) => {
          if (!obj) return null;
          const normalized = getNormalizedActivityStatus(obj);
          return normalized?.id ? Number(normalized.id) : null;
        };

        const editingStatusId = getStatusFromObj(editingFollowUp);
        const fetchedStatusId = getStatusFromObj(fetchedActivity);

        let finalStatusId = 1;
        if (editingStatusId && editingStatusId !== 6) {
          finalStatusId = editingStatusId;
        } else if (fetchedStatusId && fetchedStatusId !== 6) {
          finalStatusId = fetchedStatusId;
        } else if (editingStatusId) {
          finalStatusId = editingStatusId;
        } else if (fetchedStatusId) {
          finalStatusId = fetchedStatusId;
        }

        setStatusId(finalStatusId);
        // Handle Notes/Remarks
        // For edit, use Notes as that's what comes from the backend API
        const notesValue =
          currentData?.Remarks || currentData?.remarks|| "";
        setNotes(notesValue);

        // Handle User Assignment
        const assignedToValue =
          currentData.AssignedToId ||
          currentData.assignedToId ||
          currentData.AssignedTo ||
          currentData.assignedTo ||
          "";
        setAssignedTo(assignedToValue ? String(assignedToValue) : "");
        const rawAttendees = currentData.Attendees || currentData.attendees || [];
        let parsedAttendees = [];
        if (Array.isArray(rawAttendees)) {
          parsedAttendees = rawAttendees.map(item => {
            if (item && typeof item === 'object') {
              const id = item.UserId;
              return id ? String(id) : '';
            }
            return item ? String(item) : '';
          }).filter(Boolean);
        } else if (typeof rawAttendees === 'string') {
          parsedAttendees = rawAttendees.split(',').map(s => s.trim()).filter(Boolean);
        } else if (rawAttendees) {
          parsedAttendees = [String(rawAttendees)];
        }
        setAttendees(parsedAttendees);

        // Handle Lead Selection in edit mode:
        // Pre-select the lead's ID + all contact IDs so the hierarchical
        // dropdown shows the correct lead filtered and contacts pre-checked.
        const editLeadId = currentData?.LeadId || "";
        const editContacts = currentData?.Contacts || [];
        const editContactIds = Array.isArray(editContacts)
          ? editContacts.map(c => c.ContactId).filter(Boolean)
          : [];
        // Build the combined selection: [leadId, ...contactIds]
        // Prefix contact IDs with the lead ID to ensure uniqueness when a contact belongs to multiple leads.
        const editLeadSelection = editLeadId
          ? [String(editLeadId), ...editContactIds.map(cid => `${editLeadId}::${cid}`)]
          : editContactIds.map(cid => String(cid));
        setSelectedLead(editLeadSelection);

        // Set view date to show the month of the follow-up
        setCurrentViewDate(editDate);

        // Handle Activity Name and Title
        const actName =
          currentData.ActivityName ||
          currentData.activityName ||
          currentData._raw?.ActivityName ||
          "";
        setFollowUpName(actName);

        const titleVal =
          currentData.LeadActivityTitle ||
          currentData.leadActivityTitle ||
          currentData.title ||
          currentData.Title ||
          currentData._raw?.LeadActivityTitle ||
          "";
        setTitle(titleVal);

        // Handle details/composer states (Subject & Body pre-fill)
        const subjectValue =
          currentData?.EmailSubject ||
          currentData?.emailSubject ||
          currentData?.Subject ||
          currentData?.subject ||
          currentData?.LeadActivityTitle ||
          currentData?.title ||
          editingFollowUp?.EmailSubject ||
          editingFollowUp?.title ||
          "";
        setEmailSubject(subjectValue);

        const bodyVal =
          currentData?.EmailBody ||
          currentData?.SmsMessage ||
          currentData?.Body ||
          "";

        setEmailBody(bodyVal);
        setSmsBody(bodyVal);

        const ccVal = currentData.EmailCC || "";
        setCcList(Array.isArray(ccVal) ? ccVal : (typeof ccVal === 'string' ? ccVal.split(',').filter(Boolean) : []));

        const bccVal = currentData.EmailBCC || "";
        setBccList(Array.isArray(bccVal) ? bccVal : (typeof bccVal === 'string' ? bccVal.split(',').filter(Boolean) : []));

        const attachmentsVal = currentData.EmailAttachments || [];
        setAttachments(Array.isArray(attachmentsVal) ? attachmentsVal : []);

        const templateId = currentData.EmailTemplateId || currentData.SmsTemplateId || currentData.TemplateId || currentData.templateId || 1;
        const templatesToSearch = channelId === 1 ? mappedEmailTemplates : (channelId === 2 ? mappedSmsTemplates : []);
        const foundTemplate = templatesToSearch.find(t => String(t.id) === String(templateId));
        if (foundTemplate) {
          setEmailTemplate(foundTemplate);
        } else {
          setEmailTemplate({ id: templateId, label: templateId !== 1 ? `Template #${templateId}` : "-- No Template --" });
        }


        setPhoneNumber(currentData.CallPhoneNumber || currentData.callPhoneNumber || currentData.phoneNumber || currentData.phone || "");
        setMeetingUrl(currentData.MeetingUrl || currentData.meetingUrl || currentData.meetingURL || "");
        setAddress(currentData.SiteAddress || currentData.siteAddress || currentData.address || currentData.location || "");
        setFormattedLeadActivityNotes(currentData.FormattedLeadActivityNotes || currentData.formattedLeadActivityNotes || currentData.LeadActivityNotes || fetchedActivity?.Notes);

        const includeLeadVal =
          currentData.IncludeLeadInActivity !== undefined
            ? currentData.IncludeLeadInActivity
            : (currentData.IncludeLead !== undefined
              ? currentData.IncludeLead
              : (currentData.includeLead !== undefined
                ? currentData.includeLead
                : true));
        setIncludeLead(!!includeLeadVal);

        const prefDateRaw =
          currentData.PreferredDeliveryDate ||
          currentData.preferredDeliveryDate ||
          currentData._raw?.PreferredDeliveryDate ||
          editingFollowUp?.preferredDeliveryDate ||
          editingFollowUp?.PreferredDeliveryDate ||
          editingFollowUp?._raw?.PreferredDeliveryDate ||
          null;
        let prefDate = null;
        if (prefDateRaw && prefDateRaw !== "null" && String(prefDateRaw).trim() !== "") {
          const dateStr = typeof prefDateRaw === 'string' ? prefDateRaw.split('T')[0] : prefDateRaw;
          if (dateStr && !String(dateStr).startsWith("0001") && !String(dateStr).startsWith("1970")) {
            const parsedDate = new Date(dateStr + 'T00:00:00');
            if (!isNaN(parsedDate.getTime()) && parsedDate.getFullYear() > 2000) {
              prefDate = parsedDate;
            }
          }
        }

        const prefTimeRaw =
          currentData.PreferredDeliveryTime ||
          currentData.preferredDeliveryTime ||
          currentData._raw?.PreferredDeliveryTime ||
          editingFollowUp?.preferredDeliveryTime ||
          editingFollowUp?.PreferredDeliveryTime ||
          editingFollowUp?._raw?.PreferredDeliveryTime ||
          "";
        let prefTime = "";
        if (prefTimeRaw && prefTimeRaw !== "null" && String(prefTimeRaw).trim() !== "") {
          const rawStr = String(prefTimeRaw).trim();
          if (rawStr.includes(':')) {
            const parts = rawStr.split(':');
            prefTime = `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
          }
        }

        // Restore schedule-delivery toggle state from saved data
        // An activity has scheduled delivery ONLY IF BOTH valid date AND valid time exist
        const hasScheduledDelivery = !!(prefDate && prefTime);
        const isEditingExisting = !!(editingFollowUp || fetchedActivity);
        const isEmailOrSms = channelId == 1 || channelId == 2;

        if (!isEditingExisting && isEmailOrSms && !hasScheduledDelivery) {
          setIsScheduleDeliveryEnabled(true);
          const now = new Date();
          const currentHours = String(now.getHours()).padStart(2, '0');
          const currentMinutes = String(now.getMinutes()).padStart(2, '0');
          const currentTimeStr = `${currentHours}:${currentMinutes}`;
          setPreferredDeliveryDate(now);
          setPreferredDeliveryTime(currentTimeStr);
        } else {
          setIsScheduleDeliveryEnabled(hasScheduledDelivery);
          if (hasScheduledDelivery) {
            setPreferredDeliveryDate(prefDate);
            setPreferredDeliveryTime(prefTime);
          } else {
            setPreferredDeliveryDate(null);
            setPreferredDeliveryTime("");
          }
        }

        const actTypeId = currentData.LeadActivityTypeId || null;
        setLeadActivityTypeId(actTypeId);

        // Map ReminderBeforeMinutes (from backend) → reminderType string for the UI.
        // null / 0 / undefined → "none" (No Reminder)
        const rawMinutes = currentData.ReminderBeforeMinutes ?? currentData.reminderBeforeMinutes;
        let mappedReminderType = "none";
        if (rawMinutes) {
          if (rawMinutes <= 5) mappedReminderType = "5m";
          else if (rawMinutes <= 15) mappedReminderType = "15m";
          else if (rawMinutes <= 30) mappedReminderType = "30m";
          else if (rawMinutes <= 60) mappedReminderType = "1h";
          else mappedReminderType = "1d";
        }
        setReminderType(mappedReminderType);
      } else {
        // Reset form for new follow-up
        const dateToSet = preSelectedDate ? new Date(preSelectedDate) : new Date();
        setSelectedDate(dateToSet);
        setSelectedTime("09:00");
        setNotes("");
        setStatusId(1);

        let primaryOwnerId = extractOwnerId(leadData) || "";

        let initialAttendees = [];
        if (leadData && Array.isArray(leadData.Owners)) {
          leadData.Owners.forEach(o => {
            const oid = String(o.OwnerId || o.ownerId || "");
            if (
              oid &&
              oid !== primaryOwnerId &&
              (isAdminOrOwner || !currentLoggedInUserId || oid.toLowerCase() !== String(currentLoggedInUserId).toLowerCase()) &&
              !initialAttendees.includes(oid)
            ) {
              initialAttendees.push(oid);
            }
          });
        }
        const isCustom = isCustomActivity || followUpType === 99 || selectedChannelId === 99 || (!editingFollowUp && !preSelectedActivityType);
        const defaultAssignedTo = isCustom
          ? (currentLoggedInUserId ? String(currentLoggedInUserId) : primaryOwnerId)
          : primaryOwnerId;
        setAssignedTo(defaultAssignedTo);
        setAttendees(initialAttendees);
        setReminderType("none");
        setIsAutomated(false);
        setIncludeLead(true);
        let initialSelectedLeads = [];
        if (leadData) {
          const lId = leadData.id || leadData.leadId;
          if (lId) {
            if (Array.isArray(lId)) {
              lId.forEach(id => {
                const foundLead = availableLeads?.find(l => String(l.value) === String(id));
                const resolvedId = foundLead ? foundLead.value : id;
                initialSelectedLeads.push(resolvedId);
                if (foundLead && foundLead.contacts) {
                  foundLead.contacts.forEach(c => {
                    initialSelectedLeads.push(`${resolvedId}::${c.ContactId}`);
                  });
                }
              });
            } else {
              const foundLead = availableLeads?.find(l => String(l.value) === String(lId));
              const resolvedId = foundLead ? foundLead.value : lId;
              initialSelectedLeads.push(resolvedId);
              const contacts = leadData.Contacts || leadData.contacts || [];
              contacts.forEach(c => {
                const cid = c.ContactId || c.contactId || c.id || c.value;
                if (cid) {
                  initialSelectedLeads.push(`${resolvedId}::${cid}`);
                }
              });
              if (foundLead && foundLead.contacts) {
                foundLead.contacts.forEach(c => {
                  const cid = c.ContactId || c.contactId || c.id || c.value;
                  if (cid) {
                    const contactKey = `${resolvedId}::${cid}`;
                    if (!initialSelectedLeads.includes(contactKey)) {
                      initialSelectedLeads.push(contactKey);
                    }
                  }
                });
              }
            }
          }
        }
        setSelectedLead(initialSelectedLeads.length > 0 ? initialSelectedLeads : (leadData?.id ? [leadData.id] : []));
        setCurrentViewDate(dateToSet);
        setFollowUpName("");
        setTitle("");

        // Reset details/composer states
        setEmailSubject("");
        setEmailBody("");
        setSmsBody("");
        setCcList([]);
        setBccList([]);
        setAttachments([]);
        setEmailTemplate({ id: 1, label: "-- No Template --" });
        setPhoneNumber("");
        setMeetingUrl("");
        setAddress("");
        setFormattedLeadActivityNotes("");

        const now = new Date();
        const currentHours = String(now.getHours()).padStart(2, '0');
        const currentMinutes = String(now.getMinutes()).padStart(2, '0');
        const currentTimeStr = `${currentHours}:${currentMinutes}`;

        // If a type was pre-selected (from dropdown), skip the type-selection step
        if (preSelectedActivityType) {
          const actType = preSelectedActivityType.channelId;
          setFollowUpType(actType);
          setSelectedChannelId(actType);
          setLeadActivityTypeId(preSelectedActivityType.Id || preSelectedActivityType.id || null);

          if (actType === 1 || actType === 2) {
            setIsScheduleDeliveryEnabled(true);
            setPreferredDeliveryDate(dateToSet);
            setPreferredDeliveryTime(currentTimeStr);
          } else {
            setIsScheduleDeliveryEnabled(false);
            setPreferredDeliveryDate(null);
            setPreferredDeliveryTime("");
          }

          setStep("schedule");
        } else {
          setFollowUpType(99);
          setSelectedChannelId(99);
          setLeadActivityTypeId(null);
          setIsScheduleDeliveryEnabled(false);
          setPreferredDeliveryDate(null);
          setPreferredDeliveryTime("");
          setStep("schedule");
        }
      }
    }
  }, [isOpen, preSelectedDate, leadData, enableLeadSelection, editingFollowUp, fetchedActivity, apiActivityTypes, preSelectedActivityType, isActivityLoading]);

  // loadUsers removed — now handled by useAssignmentUsers hook above

  // Auto-select all contacts for selected leads when availableLeads loads or changes (only when creating, not editing)
  useEffect(() => {
    if (isOpen && !editingFollowUp && availableLeads.length > 0) {
      setSelectedLead(prevSelected => {
        let currentSelected = Array.isArray(prevSelected) ? [...prevSelected] : (prevSelected ? [prevSelected] : []);

        let changed = false;
        // First, normalize the selected lead IDs and filter out leads that do not have contacts
        currentSelected = currentSelected.filter(val => {
          const strVal = String(val);
          const leadIdPart = strVal.includes("::") ? strVal.split("::")[0] : strVal;
          const foundLead = availableLeads.find(l => String(l.value) === leadIdPart);
          if (foundLead) {
            if (!foundLead.contacts || foundLead.contacts.length === 0) {
              changed = true;
              return false;
            }
          }
          return true;
        }).map(val => {
          const strVal = String(val);
          if (!strVal.includes("::")) {
            const foundLead = availableLeads.find(l => String(l.value) === strVal);
            if (foundLead && foundLead.value !== val) {
              changed = true;
              return foundLead.value;
            }
          } else {
            // Normalize contact keys to match the exact lead ID type
            const [leadIdPart, contactIdPart] = strVal.split("::");
            const foundLead = availableLeads.find(l => String(l.value) === leadIdPart);
            if (foundLead && String(foundLead.value) !== leadIdPart) {
              changed = true;
              return `${foundLead.value}::${contactIdPart}`;
            }
          }
          return val;
        });

        // Now, auto-select contacts
        currentSelected.forEach(val => {
          const strVal = String(val);
          if (!strVal.includes("::")) {
            const foundLead = availableLeads.find(l => String(l.value) === strVal);
            if (foundLead && foundLead.contacts && foundLead.contacts.length > 0) {
              foundLead.contacts.forEach(c => {
                const cid = c.ContactId || c.contactId || c.id || c.value;
                if (cid) {
                  const contactKey = `${foundLead.value}::${cid}`;
                  if (!currentSelected.includes(contactKey)) {
                    currentSelected.push(contactKey);
                    changed = true;
                  }
                }
              });
            }
          }
        });

        if (changed) {
          return currentSelected;
        }
        return prevSelected;
      });
    }
  }, [availableLeads, isOpen, editingFollowUp]);


  const loadLeads = async () => {
    setLoadingLeads(true);
    try {
      const result = await getAllLeads();
      if (result.success) {
        // Transform leads data for dropdown based on API structure
        const leadsOptions = result.data.map(lead => {
          const leadName = lead.LeadName || (lead.Contacts && lead.Contacts.length > 0 ? lead.Contacts[0].FullName : "Unknown Lead");
          const primaryContact = lead.Contacts?.[0];

          return {
            value: lead.Id,
            label: leadName,
            name: leadName,
            email: primaryContact?.Email || "",
            phone: primaryContact?.PhoneNumber || primaryContact?.phoneNumber || primaryContact?.MobileNumber || primaryContact?.phone || lead.Phone || lead.PhoneNumber || lead.mobile || "",
            ownerName: lead.OwnerName,
            ownerId: lead.AssignedTo,
            contacts: lead.Contacts || []
          };
        });
        setAvailableLeads(leadsOptions);
      }
    } catch (error) {
      console.error("Error loading leads:", error);
      toast.error("Failed to load leads", {
        description: "Could not fetch available leads",
        duration: 3000,
      });
    } finally {
      setLoadingLeads(false);
    }
  };

  // Auto-patch owner when lead is selected (new activity only — must not override
  // the saved assignee when editing/rescheduling an existing activity)
  useEffect(() => {
    if (editingFollowUp || fetchedActivity) return;
    if (isCustomActivity || followUpType === 99 || selectedChannelId === 99) return;
    if (enableLeadSelection && selectedLead && Array.isArray(selectedLead) && availableLeads.length > 0 && availableUsers.length > 0) {
      // selectedLead is [leadId, ...contactIds] — find the lead whose ID is in the array
      const selectedLeadData = availableLeads.find(l => selectedLead.includes(l.value));
      if (selectedLeadData) {
        // Try to match by ID first, then by name
        if (selectedLeadData.ownerId) {
          const userById = availableUsers.find(u => u.value === selectedLeadData.ownerId);
          if (userById) {
            setAssignedTo(userById.value);
            return;
          }
        }

        if (selectedLeadData.ownerName && selectedLeadData.ownerName !== "N/A") {
          const userByName = availableUsers.find(u =>
            u.label === selectedLeadData.ownerName ||
            u.name === selectedLeadData.ownerName ||
            u.label.includes(selectedLeadData.ownerName)
          );
          if (userByName) {
            setAssignedTo(userByName.value);
          }
        }
      }
    }
  }, [selectedLead, availableLeads, availableUsers, enableLeadSelection, isCustomActivity, followUpType, selectedChannelId, editingFollowUp, fetchedActivity]);

  // Sync default Assign To with availableUsers for custom activity (channel 99)
  useEffect(() => {
    if (isOpen && (isCustomActivity || followUpType === 99 || selectedChannelId === 99) && !editingFollowUp && currentLoggedInUserId && availableUsers.length > 0) {
      const userMatch = availableUsers.find(u =>
        String(u.value).toLowerCase() === String(currentLoggedInUserId).toLowerCase() ||
        String(u.id || u.userId || "").toLowerCase() === String(currentLoggedInUserId).toLowerCase()
      );
      if (userMatch) {
        setAssignedTo(userMatch.value);
      } else if (!assignedTo) {
        setAssignedTo(String(currentLoggedInUserId));
      }
    }
  }, [isOpen, isCustomActivity, followUpType, selectedChannelId, editingFollowUp, currentLoggedInUserId, availableUsers]);

  // Clear assignedTo if assigned value (ID/GUID) is not present in availableUsers list
  useEffect(() => {
    if (assignedTo && availableUsers.length > 0) {
      const match = availableUsers.find(
        (u) =>
          String(u.value).toLowerCase() === String(assignedTo).toLowerCase() ||
          String(u.id || u.userId || "").toLowerCase() === String(assignedTo).toLowerCase() ||
          String(u.label || u.name || "").toLowerCase() === String(assignedTo).toLowerCase()
      );
      if (!match) {
        setAssignedTo("");
      } else if (match.value !== assignedTo) {
        setAssignedTo(match.value);
      }
    }
  }, [assignedTo, availableUsers]);

  // Filter out any attendee IDs/GUIDs that are not present in availableUsers list
  useEffect(() => {
    if (Array.isArray(attendees) && attendees.length > 0 && availableUsers.length > 0) {
      const validAttendees = attendees
        .filter((attId) => {
          return availableUsers.some(
            (u) =>
              String(u.value).toLowerCase() === String(attId).toLowerCase() ||
              String(u.id || u.userId || "").toLowerCase() === String(attId).toLowerCase() ||
              String(u.label || u.name || "").toLowerCase() === String(attId).toLowerCase()
          );
        })
        .map((attId) => {
          const match = availableUsers.find(
            (u) =>
              String(u.value).toLowerCase() === String(attId).toLowerCase() ||
              String(u.id || u.userId || "").toLowerCase() === String(attId).toLowerCase() ||
              String(u.label || u.name || "").toLowerCase() === String(attId).toLowerCase()
          );
          return match ? match.value : attId;
        });

      if (
        validAttendees.length !== attendees.length ||
        validAttendees.some((v, idx) => v !== attendees[idx])
      ) {
        setAttendees(validAttendees);
      }
    }
  }, [attendees, availableUsers]);

  // Follow-up type options with mapping for icons and colors
  const getMappedType = (typeValue) => {
    if (!typeValue) return { icon: CheckIcon, color: "gray" };
    const norm = typeValue.toLowerCase();
    const typeIconMap = {
      "phone call": { icon: PhoneIcon, color: "primary" },
      "email": { icon: EnvelopeIcon, color: "success" },
      "meeting": { icon: VideoCameraIcon, color: "secondary" },
      "site visit": { icon: ChatBubbleLeftEllipsisIcon, color: "info" },
      "sms": { icon: ChatBubbleLeftEllipsisIcon, color: "info" }
    };
    return typeIconMap[norm] || { icon: CheckIcon, color: "gray" };
  };

  // teamMembers is now replaced by availableUsers loaded from API


  // Utility function to check if selected date and time is in the past
  const isScheduleInPast = (date, time) => {
    const now = new Date();
    now.setSeconds(0, 0);
    const scheduleDateTime = new Date(date);
    const [hours, minutes] = time.split(':').map(Number);
    scheduleDateTime.setHours(hours, minutes, 0, 0);

    return scheduleDateTime < now;
  };

  // Auto-adjust time when date changes to ensure valid selection
  // Skip auto-adjustment in edit mode (non-reschedule) OR reschedule mode to preserve the original scheduled time
  useEffect(() => {
    if (isEditMode) return; // Don't auto-adjust time when editing an existing follow-up
    if (editingFollowUp?.isReschedule) return; // Don't auto-adjust when rescheduling — preserve the pre-filled time

    if (selectedDate && selectedTime) {
      // If the currently selected time is in the past for the selected date
      if (isScheduleInPast(selectedDate, selectedTime)) {
        const now = new Date();
        const currentHours = String(now.getHours()).padStart(2, '0');
        const currentMinutes = String(now.getMinutes()).padStart(2, '0');
        setSelectedTime(`${currentHours}:${currentMinutes}`);
      }
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    const isEmail = followUpType === 1;
    const isSms = followUpType === 2;
    const isComposer = isEmail || isSms;

    const leadsPayload = [];
    if (enableLeadSelection) {
      availableLeads.forEach(lead => {
        const isLeadSelected = selectedLead.includes(lead.value);
        const selectedContacts = lead.contacts?.filter(c => selectedLead.includes(`${lead.value}::${c.ContactId}`)) || [];

        if (isLeadSelected || selectedContacts.length > 0) {
          // When the lead is ticked without expanding its contacts, default to ALL of the
          // lead's contacts — an empty list means the backend saves no recipients and a
          // scheduled email/SMS silently fails at send time with "No email contacts".
          const contactIds = selectedContacts.length > 0
            ? selectedContacts.map(c => c.ContactId)
            : (lead.contacts || []).map(c => c.ContactId).filter(Boolean);
          leadsPayload.push({
            leadId: lead.value,
            contactIds
          });
        }
      });
    } else {
      const contacts = leadData?.Contacts || [];
      leadsPayload.push({
        leadId: leadData?.id,
        contactIds: contacts.map(c => c.ContactId || c.contactId || c.id || c.value).filter(Boolean)
      });

    }

    const leadId = enableLeadSelection ? leadsPayload.map(l => l.leadId) : (leadData?.id || editingFollowUp?.leadId || editingFollowUp?.LeadId);

    const validationErrors = {};

    // Validate required fields and character limits
    if (!followUpName || !followUpName.trim()) {
      validationErrors.followUpName = "Activity Name is required";
    } else if (followUpName.length > 200) {
      validationErrors.followUpName = "Activity Name cannot exceed 200 characters";
    }

    if (!isComposer && !selectedDate) {
      validationErrors.selectedDate = "Date is required";
    }

    if (!isComposer && !selectedTime) {
      validationErrors.selectedTime = "Time is required";
    } else if (!isEditMode && !isComposer && !restrictedEditMode && selectedDate && isScheduleInPast(selectedDate, selectedTime)) {
      validationErrors.selectedTime = "Cannot schedule activity in the past";
    }

    const isValidAssignee = Array.isArray(availableUsers) && availableUsers.length > 0
      ? availableUsers.some(user => String(user.value) === String(assignedTo))
      : true;
    if (!assignedTo || String(assignedTo).trim() === "" || String(assignedTo) === "undefined" || String(assignedTo) === "null" || !isValidAssignee) {
      validationErrors.assignedTo = "Assignee is required";
    }

    if (editingFollowUp && !statusId) {
      validationErrors.statusId = "Activity Status is required";
    }

    if (!isCustomActivity) {
      if (enableLeadSelection) {
        if (leadsPayload.length === 0) {
          validationErrors.selectedLead = "At least one lead is required";
        }
      } else if (!leadId) {
        validationErrors.selectedLead = "A lead is required for this activity";
      }
    }

    if (notes && notes.length > 500) {
      validationErrors.notes = "Remarks cannot exceed 500 characters";
    }

    if (formattedLeadActivityNotes && formattedLeadActivityNotes.length > 500) {
      validationErrors.formattedLeadActivityNotes = "Detailed notes cannot exceed 500 characters (including HTML formatting)";
    }

    // Channel-specific validations
    if (followUpType === 1) {
      if (!emailSubject || !emailSubject.trim()) {
        validationErrors.emailSubject = "Subject is required";
      } else if (emailSubject.length > 200) {
        validationErrors.emailSubject = "Subject cannot exceed 200 characters";
      }
      if (!emailBody || !stripHtml(emailBody)) {
        validationErrors.emailBody = "Email body is required";
      } else if (emailBody.length > 2000) {
        validationErrors.emailBody = "Email body cannot exceed 2000 characters (including HTML formatting)";
      }
      if (ccList && ccList.length > 0) {
        const ccString = ccList.join(',');
        if (ccString.length > 500) {
          validationErrors.ccList = "CC list cannot exceed 500 characters";
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidCc = ccList.filter(email => !emailRegex.test(email));
        if (invalidCc.length > 0) {
          validationErrors.ccList = `Invalid email address(es): ${invalidCc.join(', ')}`;
        }
      }
      if (bccList && bccList.length > 0) {
        const bccString = bccList.join(',');
        if (bccString.length > 500) {
          validationErrors.bccList = "BCC list cannot exceed 500 characters";
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidBcc = bccList.filter(email => !emailRegex.test(email));
        if (invalidBcc.length > 0) {
          validationErrors.bccList = `Invalid email address(es): ${invalidBcc.join(', ')}`;
        }
      }
    } else if (followUpType === 2) {
      if (!smsBody || !stripHtml(smsBody)) {
        validationErrors.smsBody = "SMS body is required";
      } else {
        const smsText = stripHtml(smsBody);
        if (smsText.length > 500) {
          validationErrors.smsBody = "SMS body cannot exceed 500 characters";
        }
      }
    } else if (followUpType === 3) {
      if (!phoneNumber || !phoneNumber.trim()) {
        validationErrors.phoneNumber = "Phone Number is required";
      } else {
        if (phoneNumber.startsWith("+")) {
          if (!isValidPhoneNumber(phoneNumber)) {
            validationErrors.phoneNumber = "Please enter a valid international phone number";
          }
        } else {
          const digitsOnly = phoneNumber.replace(/\D/g, "");
          if (digitsOnly.length !== 10) {
            validationErrors.phoneNumber = "Phone Number must be exactly 10 digits";
          }
        }
      }
    } else if (followUpType === 4) {
      if (!meetingUrl || !meetingUrl.trim()) {
        validationErrors.meetingUrl = "Meeting URL is required";
      } else if (meetingUrl.length > 500) {
        validationErrors.meetingUrl = "Meeting URL cannot exceed 500 characters";
      } else {
        try {
          new URL(meetingUrl);
        } catch (_) {
          validationErrors.meetingUrl = "Please enter a valid URL (e.g., https://zoom.us/j/...)";
        }
      }
    } else if (followUpType === 5) {
      if (!address || !address.trim()) {
        validationErrors.address = "Address is required";
      } else if (address.length > 500) {
        validationErrors.address = "Address cannot exceed 500 characters";
      }
    } else if (followUpType === 99) {
      if (!title || !title.trim()) {
        validationErrors.title = "Title is required";
      } else if (title.length > 200) {
        validationErrors.title = "Title cannot exceed 200 characters";
      }
    }

    if ((followUpType === 1 || followUpType === 2) && isScheduleDeliveryEnabled) {
      if (!preferredDeliveryDate || !preferredDeliveryTime) {
        validationErrors.preferredDeliveryDate = "Delivery date and time are required when scheduling is enabled";
      } else {
        const deliveryDateTime = new Date(preferredDeliveryDate);
        const [delHours, delMinutes] = (preferredDeliveryTime || "00:00").split(':').map(Number);
        deliveryDateTime.setHours(delHours, delMinutes, 0, 0);
        const nowLimit = new Date();
        nowLimit.setSeconds(0, 0);
        if (!isEditMode && deliveryDateTime < nowLimit) {
          validationErrors.preferredDeliveryDate = "Scheduled delivery must be in the future";
        }
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const errorMessages = Object.values(validationErrors).filter(Boolean);
      const firstError = errorMessages[0];
      const additionalCount = errorMessages.length - 1;

      toast.error(firstError, {
        description: additionalCount > 0 ? `And ${additionalCount} other issue(s)` : undefined,
      });
      return;
    }

    setErrors({});

    if (!followUpType) {
      toast.error("Please select activity type", {
        description: "Choose the type of activity (call, email, etc.)",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // isDetails: any channel that isn't email/sms — includes standard (3-7) and custom base types
      const isDetails = !isComposer;

      const followUpData = {
        leadId: leadId,
        leads: leadsPayload,
        date: isComposer
          ? (isScheduleDeliveryEnabled
              ? (preferredDeliveryDate instanceof Date ? preferredDeliveryDate : (preferredDeliveryDate ? new Date(preferredDeliveryDate + 'T00:00:00') : (selectedDate || new Date())))
              : (selectedDate || new Date()))
          : selectedDate,
        time: isComposer
          ? (isScheduleDeliveryEnabled
              ? (preferredDeliveryTime || selectedTime || "09:00")
              : (selectedTime || "09:00"))
          : selectedTime,
        type: followUpType,
        activityName: followUpName,
        title: title,
        leadActivityTitle: title,
        // Send channelId from selection, or preserve edit channelId
        channelId: editingFollowUp
          ? (editingFollowUp.channelId || editingFollowUp.ChannelId)
          : followUpType || selectedChannelId,
        notes: notes,
        detailedNotes: stripHtml(formattedLeadActivityNotes),
        leadActivityNotes: stripHtml(formattedLeadActivityNotes),
        formattedLeadActivityNotes: formattedLeadActivityNotes,
        assignedTo,
        attendees: Array.isArray(attendees) ? attendees.join(',') : attendees,
        reminderType,
        isAutomated,
        IncludeLeadInActivity: includeLead,
        preferredDeliveryDate: isComposer && isScheduleDeliveryEnabled ? preferredDeliveryDate : null,
        preferredDeliveryTime: isComposer && isScheduleDeliveryEnabled ? preferredDeliveryTime : null,
        LeadActivityTypeId: leadActivityTypeId,
        // Include email/sms composition data if applicable
        ...(isComposer ? {
          subject: emailSubject,
          body: isEmail ? emailBody : stripHtml(smsBody),
          cc: ccList,
          bcc: bccList,
          attachments: attachments,
          templateId: emailTemplate?.id !== 1 ? emailTemplate?.id : null
        } : {}),
        ...(isDetails ? {
          phoneNumber: phoneNumber,
          meetingUrl: meetingUrl,
          address: address
        } : {}),
        contactIds: (() => {
          const rLeadId = editingFollowUp?.LeadId || editingFollowUp?.leadId || leadData?.id;
          return Array.isArray(selectedLead)
            ? selectedLead
              .filter(id => String(id) !== String(rLeadId))
              .map(id => {
                const strId = String(id);
                if (strId.includes("::")) {
                  return strId.split("::")[1];
                }
                return strId;
              })
            : [];
        })(),
        statusId: statusId
      };

      let result;
      if (isCustomActivity && isJobActivity) {
        // Job Calendar Custom Activity — always lead-less, unchanged upsert-only path
        let reminderBeforeMinutes = null;
        if (reminderType === "15m" || reminderType === "15_min") reminderBeforeMinutes = 15;
        else if (reminderType === "30m" || reminderType === "30_min") reminderBeforeMinutes = 30;
        else if (reminderType === "1h" || reminderType === "1_hour") reminderBeforeMinutes = 60;
        else if (reminderType === "1d" || reminderType === "1_day") reminderBeforeMinutes = 1440;
        else if (reminderType === "5m" || reminderType === "5_min") reminderBeforeMinutes = 5;
        else if (!isNaN(Number(reminderType))) reminderBeforeMinutes = Number(reminderType);

        const customPayload = {
          followUpId: editingFollowUp ? (editingFollowUp.id || editingFollowUp.Id || editingFollowUp.FollowUpId || editingFollowUp.followUpId) : null,
          assignedTo: assignedTo || getUserIdFromToken(),
          title: title || followUpName || null,
          activityName: followUpName || title || null,
          notes: formattedLeadActivityNotes || null,
          remarks: notes,
          scheduledDate: selectedDate,
          scheduledTime: selectedTime,
          reminderBeforeMinutes: reminderBeforeMinutes,
          clearReminderMinutes: reminderType === "none" || reminderBeforeMinutes === null,
          attendeeIds: Array.isArray(attendees) ? attendees : [],
          priority: editingFollowUp?.priority || editingFollowUp?.Priority || "Normal",
          statusId: statusId || 1,
          isJobActivity: isJobActivity || false
        };

        result = await upsertCustomActivity(customPayload);
      } else if (editingFollowUp) {
        result = await followUpActions.reschedule({
          followUpId: editingFollowUp.id || editingFollowUp.Id,
          ...followUpData,
          statusId: statusId,
          channelId: editingFollowUp.channelId || editingFollowUp.ChannelId || selectedChannelId
        });
      } else {
        result = await followUpActions.create(followUpData);
      }

      if (result?.success) {
        if (isCustomActivity) {
          toast.success(
            result?.message ||
            (editingFollowUp
              ? "Custom activity updated successfully"
              : "Custom activity created successfully")
          );
        }
        if (onSuccess) {
          onSuccess(result);
        }
        onClose();
      }
    } catch (error) {
      console.error("Error scheduling follow-up:", error);
      toast.error("Failed to schedule activity", {
        description: error.message || "An unexpected error occurred",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeIcon = (type) => {
    return getMappedType(type).icon;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => { }}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className={`${step === "select_type" ? "max-w-5xl" : "max-w-7xl"} relative flex w-full origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none`}>
            {/* Header */}
            <div className="rounded-t-lg bg-gray-200 dark:bg-dark-800 px-4 py-3 sm:px-5">
              {/* Row 1: icon + title + close button */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <CalendarDaysIcon className="h-5 w-5 shrink-0 text-gray-600 dark:text-dark-300" />
                  <DialogTitle as="h3" className="truncate text-base font-medium text-gray-800 dark:text-dark-100">
                    {restrictedEditMode
                      ? "Update Activity"
                      : step === "select_type"
                        ? "Select Activity Type"
                        : editingFollowUp
                          ? "Update Activity"
                          : "Schedule Activity"
                    }
                  </DialogTitle>
                </div>
                <Button
                  onClick={onClose}
                  variant="flat"
                  isIcon
                  className="size-7 shrink-0 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
                >
                  <XMarkIcon className="size-4.5" />
                </Button>
              </div>

              {/* Row 2: subtitle + toggle */}
              <div className="mt-1.5 flex items-center justify-between gap-3">
                <p className="truncate text-xs text-gray-500 dark:text-dark-400 sm:text-sm sm:text-gray-600 dark:sm:text-dark-300">
                  {restrictedEditMode
                    ? `Update status and remarks for ${(editingFollowUp?.userName && editingFollowUp.userName !== "Unknown Lead" && editingFollowUp.userName !== "Unknown" && editingFollowUp.userName !== "-" ? editingFollowUp.userName : null) || (leadData?.name && leadData.name !== "Unknown Lead" && leadData.name !== "Unknown" && leadData.name !== "-" ? leadData.name : null) || 'Lead'}`
                    : step === "select_type"
                      ? "Choose the type of activity you want to schedule"
                      : editingFollowUp || leadData
                        ? (displayLeadName && displayLeadName !== "-" && displayLeadName !== "Unknown Lead" && displayLeadName !== "Unknown" && displayLeadName !== "Lead" ? `for ${displayLeadName}` : "")
                        : isCustomActivity
                          ? ""
                          : "Select a lead and schedule activity"
                  }
                </p>
                {step !== "select_type" && !isCustomActivity && (
                  <Switch
                    label={
                      <>
                        <span className="sm:hidden text-xs text-gray-600 dark:text-dark-300 whitespace-nowrap">Include Lead</span>
                        <span className="hidden sm:inline text-sm text-gray-600 dark:text-dark-300 whitespace-nowrap">Include Lead in Activity</span>
                      </>
                    }
                    checked={includeLead}
                    onChange={(e) => setIncludeLead(e.target.checked)}
                    color="primary"
                    disabled={restrictedEditMode || isReadOnlyMode}
                  />
                )}
              </div>
            </div>
            <>
              <div className="flex flex-col overflow-y-auto min-h-[350px] pb-32 sm:pb-8">
                {isActivityLoading ? (
                  <ActivityDetailSkeleton />
                ) : activityError ? (
                  <div className="flex flex-col items-center justify-center flex-1 py-20 px-4 text-center">
                    <p className="text-sm font-medium text-red-500 dark:text-red-400">
                      Failed to load activity details. Please try again.
                    </p>
                  </div>
                ) : (
                  <>
                    {isAlreadyCompleted && (
                      <div className="mx-4 sm:mx-6 mt-6 mb-2 rounded-xl bg-amber-50 p-4 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 flex items-start gap-3">
                        <InformationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5 shrink-0" />
                        <div>
                          <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Activity Completed</h4>
                          <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">This activity has been marked as completed. You can view the details below, but it can no longer be updated or rescheduled.</p>
                        </div>
                      </div>
                    )}

                    {!isAlreadyCompleted && isPermissionReadOnly && !isAttendeeViewOnly && (
                      <div className="mx-4 sm:mx-6 mt-6 mb-2 rounded-xl bg-amber-50 p-4 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 flex items-start gap-3">
                        <InformationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5 shrink-0" />
                        <div>
                          <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300">No Access</h4>
                          <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">You have no access to update the details of this activity. You can only view it.</p>
                        </div>
                      </div>
                    )}

                    {!isAlreadyCompleted && isAttendeeViewOnly && (
                      <div className="mx-4 sm:mx-6 mt-6 mb-2 rounded-xl bg-blue-50 p-4 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 flex items-start gap-3">
                        <InformationCircleIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mt-0.5 shrink-0" />
                        <div>
                          <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300">View Only</h4>
                          <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">You do not have permission to edit this activity. You can only view its details because you are an attendee.</p>
                        </div>
                      </div>
                    )}

                    <div className={clsx(
                      "grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-200 dark:divide-dark-600",
                      isReadOnlyMode && "pointer-events-none opacity-60 select-none"
                    )}>
                      <CalendarSection
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        reminderType={reminderType}
                        setReminderType={setReminderType}
                        isEditMode={isEditMode}
                        selectedTime={selectedTime}
                        setSelectedTime={setSelectedTime}
                        timeSlots={timeSlots}
                        isScheduleInPast={isScheduleInPast}
                        editingFollowUp={editingFollowUp}
                        channelId={selectedChannelId}
                        followUpName={followUpName}
                        setFollowUpName={setFollowUpName}
                        enableLeadSelection={enableLeadSelection}
                        isJobActivity={isJobActivity}
                        selectedLead={selectedLead}
                        setSelectedLead={setSelectedLead}
                        loadingLeads={loadingLeads}
                        availableLeads={availableLeads}
                        assignedTo={assignedTo}
                        setAssignedTo={setAssignedTo}
                        attendees={attendees}
                        setAttendees={setAttendees}
                        loadingUsers={loadingUsers}
                        availableUsers={availableUsers}
                        setPendingAssignedTo={setPendingAssignedTo}
                        setShowReassignModal={setShowReassignModal}
                        leadData={leadData}
                        errors={errors}
                        setErrors={setErrors}
                        notes={notes}
                        setNotes={setNotes}
                        statusId={statusId}
                        setStatusId={setStatusId}
                        restrictedEditMode={restrictedEditMode}
                        isReminderDisabled={isEditMode && ((fetchedActivity?.ReminderBeforeMinutes ?? fetchedActivity?.reminderBeforeMinutes ?? 0) > 0)}
                        currentLoggedInUserId={currentLoggedInUserId}
                        isAdminOrOwner={isAdminOrOwner}
                      />

                      {/* Details Section — shown always, disabled in restrictedEditMode */}
                      <div className={restrictedEditMode ? "col-span-2 lg:col-span-2 opacity-50 pointer-events-none select-none" : "col-span-2 lg:col-span-2"}>
                        <DetailsSection
                          followUpType={followUpType}
                          selectedLeadObj={selectedLeadObj}
                          emailSubject={emailSubject}
                          setEmailSubject={setEmailSubject}
                          emailBody={emailBody}
                          setEmailBody={setEmailBody}
                          smsBody={smsBody}
                          setSmsBody={setSmsBody}
                          ccList={ccList}
                          setCcList={setCcList}
                          bccList={bccList}
                          setBccList={setBccList}
                          attachments={attachments}
                          setAttachments={setAttachments}
                          phoneNumber={phoneNumber}
                          setPhoneNumber={setPhoneNumber}
                          meetingUrl={meetingUrl}
                          setMeetingUrl={setMeetingUrl}
                          address={address}
                          setAddress={setAddress}
                          detailedNotes={formattedLeadActivityNotes}
                          setDetailedNotes={setFormattedLeadActivityNotes}
                          preferredDeliveryDate={preferredDeliveryDate}
                          setPreferredDeliveryDate={setPreferredDeliveryDate}
                          preferredDeliveryTime={preferredDeliveryTime}
                          setPreferredDeliveryTime={setPreferredDeliveryTime}
                          emailTemplate={emailTemplate}
                          setEmailTemplate={setEmailTemplate}
                          channelId={selectedChannelId}
                          title={title}
                          setTitle={setTitle}
                          followUp={fetchedActivity || editingFollowUp}
                          isScheduleDisabled={restrictedEditMode || (isEditMode && !editingFollowUp?.isReschedule)}
                          isScheduleDeliveryEnabled={isScheduleDeliveryEnabled}
                          setIsScheduleDeliveryEnabled={setIsScheduleDeliveryEnabled}
                          errors={errors}
                          setErrors={setErrors}
                          emailTemplates={mappedEmailTemplates}
                          smsTemplates={mappedSmsTemplates}
                          templatesLoading={templatesLoading}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 dark:bg-dark-800 px-4 py-4 sm:px-6 flex items-center justify-between gap-3 border-t border-gray-200 dark:border-dark-600">
                <div className="flex items-center gap-2 sm:gap-3">
                  {(editingFollowUp || fetchedActivity) && targetLeadId && !hasMultipleLeads && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleNavigateToLead}
                      className="flex items-center gap-1.5 text-xs sm:text-sm"
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      Navigate to Lead
                    </Button>
                  )}
                  {canUserDelete && (editingFollowUp || fetchedActivity) && !isReadOnlyMode && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setShowDeleteConfirmModal(true)}
                      disabled={isDeleting || isSubmitting}
                      className="flex items-center gap-1.5 text-xs sm:text-sm text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20"
                    >
                      <TrashIcon className="h-4 w-4" />
                      {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {!editingFollowUp && (
                    <Button
                      variant="outlined"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  )}
                  {!isReadOnlyMode && (
                    <Button
                      color="primary"
                      onClick={handleSubmit}
                      disabled={isSubmitting || isActivityLoading}
                      className="flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          {editingFollowUp
                            ? (editingFollowUp.isReschedule ? "Rescheduling..." : "Updating...")
                            : "Scheduling..."
                          }
                        </>
                      ) : (
                        <>
                          <CheckIcon className="h-4 w-4" />
                          {editingFollowUp
                            ? (editingFollowUp.isReschedule ? "Update Activity" : "Update Activity")
                            : "Schedule Activity"
                          }
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </>
          </DialogPanel>
        </TransitionChild>
      </Dialog>

      <ConfirmModal
        show={showReassignModal}
        onClose={() => {
          setShowReassignModal(false);
          setAssignedTo(pendingAssignedTo);
        }}
        onOk={async () => {
          const leadId = enableLeadSelection
            ? selectedLead.filter(id => availableLeads.some(l => String(l.value) === String(id)))
            : (leadData?.id || editingFollowUp?.leadId || editingFollowUp?.LeadId);
          setReassignLoading(true);
          try {
            const result = await updateLeadOwner(leadId, pendingAssignedTo);
            if (result.success) {
              setReassignState("success");
              setAssignedTo(pendingAssignedTo);
              // Update local leads data if available
              if (enableLeadSelection) {
                const leadIdsArray = Array.isArray(leadId) ? leadId : [leadId];
                setAvailableLeads(prev => prev.map(l =>
                  leadIdsArray.includes(l.value) ? { ...l, ownerId: pendingAssignedTo } : l
                ));
              }

              // Close the modal after a short delay on success
              setTimeout(() => {
                setShowReassignModal(false);
                setReassignState("pending");
              }, 1500);
            } else {
              setReassignState("error");
              toast.error(result.error || "Failed to update lead owner");
            }
          } catch (error) {
            console.error("Error updating lead owner:", error);
            setReassignState("error");
            toast.error("An error occurred while updating lead owner");
          } finally {
            setReassignLoading(false);
          }
        }}
        confirmLoading={reassignLoading}
        state={reassignState}
        messages={{
          pending: {
            title: "Reassign Lead Owner?",
            description: "This owner is not assigned to this lead. Are you sure you want to reassign this owner to the lead?",
            actionText: "Yes, Reassign"
          },
          success: {
            title: "Lead Owner Reassigned",
            description: "The lead owner has been updated successfully."
          }
        }}
      />

      <ConfirmModal
        show={showDeleteConfirmModal}
        onClose={() => setShowDeleteConfirmModal(false)}
        onOk={handleDeleteActivity}
        confirmLoading={isDeleting}
        messages={{
          pending: {
            title: "Delete Activity?",
            description: "Are you sure you want to delete this activity? This action cannot be undone.",
            actionText: "Yes, Delete"
          },
          success: {
            title: "Activity Deleted",
            description: "The activity has been deleted successfully."
          }
        }}
      />
    </Transition>
  );
};

export default FollowUpScheduleModal;
